import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ChevronDown, Plus, ShieldCheck } from "lucide-react";
import { PsvMonitoringProgramStatus } from "@api/shared-runtime/psv-monitoring";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { retryUnlessRecordUnavailable } from "@/common/utils/record-unavailable-error";
import { useTRPC } from "@/trpc";
import {
  FEATURE_NAME,
  MONITORING_COPY,
  SET_UP_STANDARD_LABEL,
} from "./monitoring-constants";
import { isConflictError, isForbiddenError } from "./monitoring-format";
import type { MonitoringProgram } from "./monitoring-types";
import { RuleEditorSheet } from "./monitoring-page/rule-editor-sheet";
import { DigestSubscribeBanner } from "./monitoring-page/digest-subscribe-banner";
import { ReportAddressCard } from "./monitoring-page/report-address-card";
import { RulesTable } from "./monitoring-page/rules-table";
import { useInvalidateMonitoring } from "./use-monitoring-invalidation";

type Props = {
  organizationBusinessUnitId: string;
  canEdit: boolean;
};

type EditorState = {
  /** Null creates a rule. */
  programId: string | null;
  startAsStandard: boolean;
  /** Bumped by Reload so the editor remounts on the latest saved rule. */
  nonce: number;
};

const SECTION_HEADING_ID = "monthly-monitoring-heading";

type PendingArchive = MonitoringProgram | null;

export function MonitoringPage({ organizationBusinessUnitId, canEdit }: Props) {
  const trpc = useTRPC();
  const invalidate = useInvalidateMonitoring();
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [pendingArchive, setPendingArchive] = useState<PendingArchive>(null);
  const query = useQuery(
    trpc.psv.monitoring.listPrograms.queryOptions(
      { organizationBusinessUnitId },
      { retry: retryUnlessRecordUnavailable },
    ),
  );
  const setStatus = useMutation(
    trpc.psv.monitoring.setProgramStatus.mutationOptions({
      onSuccess: async () => {
        await invalidate();
        toast.success("Rule updated.");
      },
      onError: async (error) => {
        if (isConflictError(error)) {
          await invalidate();
          toast.error(MONITORING_COPY.staleRule);
          return;
        }
        toast.error(error.message);
      },
    }),
  );

  if (isForbiddenError(query.error)) {
    return (
      <PageShell>
        <Card className="border-dashed">
          <CardContent className="p-8 text-center text-slate-600">
            {MONITORING_COPY.managedByAdmins}
          </CardContent>
        </Card>
      </PageShell>
    );
  }
  if (query.isPending) {
    return (
      <PageShell>
        <RulesTableSkeleton />
      </PageShell>
    );
  }
  if (query.isError) {
    return (
      <PageShell>
        <Card className="border-dashed">
          <CardContent className="space-y-3 p-8 text-center">
            <p role="alert">Couldn't load monitoring rules.</p>
            <Button variant="outline" onClick={() => void query.refetch()}>
              Try again
            </Button>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  const programs = query.data;
  const liveRules = programs.filter(
    (program) => program.status !== PsvMonitoringProgramStatus.Archived,
  );
  const archivedRules = programs.filter(
    (program) => program.status === PsvMonitoringProgramStatus.Archived,
  );
  const editedProgram =
    programs.find((program) => program.id === editor?.programId) ?? null;

  function changeStatus({
    program,
    status,
  }: {
    program: MonitoringProgram;
    status: PsvMonitoringProgramStatus;
  }) {
    if (status === PsvMonitoringProgramStatus.Archived) {
      setPendingArchive(program);
      return;
    }
    setStatus.mutate({
      organizationBusinessUnitId,
      programId: program.id,
      expectedRevision: program.revision,
      status,
    });
  }

  function confirmArchive() {
    if (!pendingArchive) {
      return;
    }
    setStatus.mutate({
      organizationBusinessUnitId,
      programId: pendingArchive.id,
      expectedRevision: pendingArchive.revision,
      status: PsvMonitoringProgramStatus.Archived,
    });
    setPendingArchive(null);
  }

  async function reloadEditor() {
    await query.refetch();
    setEditor((current) =>
      current ? { ...current, nonce: current.nonce + 1 } : current,
    );
  }

  return (
    <PageShell
      action={
        canEdit && programs.length > 0 ? (
          <Button
            onClick={() =>
              setEditor({ programId: null, startAsStandard: false, nonce: 0 })
            }
          >
            <Plus size={16} className="mr-2" />
            New rule
          </Button>
        ) : null
      }
    >
      <DigestSubscribeBanner
        organizationBusinessUnitId={organizationBusinessUnitId}
      />
      {programs.length === 0 && (
        <EmptyState
          canEdit={canEdit}
          onSetUp={() =>
            setEditor({ programId: null, startAsStandard: true, nonce: 0 })
          }
        />
      )}
      {liveRules.length > 0 && (
        <RulesTable
          programs={liveRules}
          canEdit={canEdit}
          onEdit={(program) =>
            setEditor({
              programId: program.id,
              startAsStandard: false,
              nonce: 0,
            })
          }
          onSetStatus={changeStatus}
        />
      )}
      {archivedRules.length > 0 && (
        <Collapsible className="space-y-2">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm">
              <ChevronDown className="mr-1 size-4" aria-hidden="true" />
              Archived rules ({archivedRules.length})
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <RulesTable
              programs={archivedRules}
              readOnly
              canEdit={canEdit}
              onEdit={() => undefined}
              onSetStatus={changeStatus}
            />
          </CollapsibleContent>
        </Collapsible>
      )}
      <ReportAddressCard
        organizationBusinessUnitId={organizationBusinessUnitId}
        canEdit={canEdit}
      />
      {editor && (!editor.programId || editedProgram) && (
        <RuleEditorSheet
          key={editor.nonce}
          organizationBusinessUnitId={organizationBusinessUnitId}
          program={editedProgram}
          startAsStandard={editor.startAsStandard}
          onClose={() => setEditor(null)}
          onReload={() => void reloadEditor()}
        />
      )}
      {pendingArchive && (
        <AlertDialog
          open
          onOpenChange={(open) => !open && setPendingArchive(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Archive {pendingArchive.name}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                {MONITORING_COPY.archiveWarning}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmArchive}>
                Archive
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </PageShell>
  );
}

function PageShell({
  action,
  children,
}: {
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section aria-labelledby={SECTION_HEADING_ID} className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2
            id={SECTION_HEADING_ID}
            className="text-xl font-semibold text-slate-900"
          >
            {FEATURE_NAME}
          </h2>
          <p className="mt-1 text-sm text-slate-600 sm:text-base">
            Rules that re-check providers' credentials every month.
          </p>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

const RULE_SKELETON_ROW_COUNT = 2;

/** Mirrors the rules table: header row, then name, status badge, sources and
 * coverage per rule. */
function RulesTableSkeleton() {
  return (
    <div role="status" aria-busy="true">
      <span className="sr-only">Loading monitoring rules…</span>
      <Card aria-hidden className="overflow-hidden p-0">
        <div className="h-10 border-b bg-gray-50" />
        {Array.from({ length: RULE_SKELETON_ROW_COUNT }, (_, index) => (
          <div
            key={index}
            className="flex items-center gap-6 border-b p-2 last:border-b-0"
          >
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-6 w-20 rounded-full" />
            <Skeleton className="h-5 flex-1" />
            <Skeleton className="h-5 w-24" />
          </div>
        ))}
      </Card>
    </div>
  );
}

function EmptyState({
  canEdit,
  onSetUp,
}: {
  canEdit: boolean;
  onSetUp: () => void;
}) {
  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center gap-4 p-8 text-center md:p-12">
        <div className="rounded-full bg-primary/10 p-3">
          <ShieldCheck className="h-8 w-8 text-primary" aria-hidden="true" />
        </div>
        <div>
          <h3 className="mb-1 text-xl font-semibold text-slate-900">
            No monitoring rules yet
          </h3>
          <p className="max-w-md text-slate-600">
            {MONITORING_COPY.emptyPageReason}
          </p>
        </div>
        {canEdit && <Button onClick={onSetUp}>{SET_UP_STANDARD_LABEL}</Button>}
      </CardContent>
    </Card>
  );
}
